import { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "@/common/API"

interface SyncHealthProvider {
  name: string
  vehicleCount: number
  lastSync?: string
  healthy: boolean
}

interface SyncHealth {
  vehiclesSyncing: number
  lastSync?: string
  stopsToday: number
  errorsLast24h: number
  providers: SyncHealthProvider[]
}

interface SyncLogRecord {
  id: number
  timer: string
  organizationId?: string
  startedAt: string
  finishedAt?: string
  durationMs?: number
  recordsRead: number
  recordsUpserted: number
  recordsSkipped: number
  errorMessage?: string
}

interface OrganizationOption {
  id: string
  name: string
}

const triggerActions = [
  { key: "vehicleStates", endpoint: "Sync/TriggerVehicleStates", labelKey: "Trigger Vehicle States", icon: "fa-location-dot" },
  { key: "transactional", endpoint: "Sync/TriggerTransactional", labelKey: "Trigger Transactional Sync", icon: "fa-file-invoice-dollar" },
  { key: "dispatchDetails", endpoint: "Sync/TriggerDispatchDetails", labelKey: "Trigger Dispatch Detail Sync", icon: "fa-truck-ramp-box" },
  { key: "masters", endpoint: "Sync/TriggerMasters", labelKey: "Trigger Masters Sync", icon: "fa-database" },
  { key: "fleetMasters", endpoint: "Sync/TriggerFleetMasters", labelKey: "Trigger Fleet Masters", icon: "fa-truck-field" },
] as const

const SyncMonitorComponent = () => {
  const { t } = useTranslation("global")
  const [health, setHealth] = useState<SyncHealth | null>(null)
  const [logs, setLogs] = useState<SyncLogRecord[]>([])
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isTriggering, setIsTriggering] = useState<string | null>(null)
  const [selectedTimer, setSelectedTimer] = useState("")
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("")
  const [feedback, setFeedback] = useState<{ type: "success" | "danger"; message: string } | null>(null)

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      const qs = new URLSearchParams()
      if (selectedOrganizationId) {
        qs.set("OrganizationId", selectedOrganizationId)
      }
      const filterParams = qs.toString()

      const [healthRes, logRes, orgRes] = await Promise.all([
        API.getAction("Sync/Health", filterParams || undefined),
        API.getAction("Sync/Log", filterParams || undefined),
        API.getAction("Organization/AdminList", "Page=0&Quantity=200"),
      ])

      if (healthRes.data.status === "ok") {
        setHealth(healthRes.data.response)
      }

      if (logRes.data.status === "ok") {
        setLogs(logRes.data.response ?? [])
      }

      if (orgRes.data.status === "ok") {
        setOrganizations((orgRes.data.response ?? []).map((item: any) => ({
          id: item.id,
          name: item.name,
        })))
      }
    } catch (ex) {
      console.error(ex)
      setFeedback({ type: "danger", message: t("Unable to load sync monitor") })
    } finally {
      setIsLoading(false)
    }
  }, [selectedOrganizationId, t])

  useEffect(() => {
    loadData()
    const intervalId = window.setInterval(loadData, 30000)
    return () => window.clearInterval(intervalId)
  }, [loadData])

  const organizationMap = useMemo(
    () => Object.fromEntries(organizations.map(org => [org.id, org.name])),
    [organizations]
  )

  const availableTimers = useMemo(
    () => [...new Set(logs.map(log => log.timer))].sort(),
    [logs]
  )

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (selectedTimer && log.timer !== selectedTimer) return false
      return true
    })
  }, [logs, selectedTimer])

  const runTrigger = useCallback(async (endpoint: string, key: string) => {
    try {
      setIsTriggering(key)
      setFeedback(null)
      const qs = selectedOrganizationId ? `OrganizationId=${encodeURIComponent(selectedOrganizationId)}` : undefined
      const res = await API.postAction(qs ? `${endpoint}?${qs}` : endpoint, null)
      if (res.data.status === "ok") {
        setFeedback({ type: "success", message: res.data.response || t("Sync trigger queued") })
        window.setTimeout(() => {
          loadData()
        }, 1500)
        return
      }

      setFeedback({ type: "danger", message: res.data.response || t("Unable to trigger sync") })
    } catch (ex: any) {
      console.error(ex)
      setFeedback({ type: "danger", message: ex?.response?.data?.response || t("Unable to trigger sync") })
    } finally {
      setIsTriggering(null)
    }
  }, [loadData, selectedOrganizationId, t])

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h5 className="fw-bold mb-0">
            <i className="fas fa-rotate me-2 text-primary" />
            {t("Sync Center")}
          </h5>
          <small className="text-muted">{t("Monitor and trigger platform synchronizations")}</small>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={isLoading}>
          <i className={`fas ${isLoading ? "fa-spinner fa-spin" : "fa-arrows-rotate"} me-1`} />
          {t("Refresh")}
        </Button>
      </div>

      {feedback && (
        <Alert variant={feedback.type} className="mb-4">
          {feedback.message}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white fw-semibold">
              <i className="fas fa-building me-2 text-muted" />
              {t("View Scope")}
            </Card.Header>
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={6} lg={4}>
                  <Form.Label className="small text-muted mb-1">{t("Organization")}</Form.Label>
                  <Form.Select size="sm" value={selectedOrganizationId} onChange={e => setSelectedOrganizationId(e.target.value)}>
                    <option value="">{t("All Organizations")}</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md="auto">
                  <div className="small text-muted">
                    {selectedOrganizationId
                      ? t("Showing sync data for the selected organization")
                      : t("Showing sync data across all organizations")}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        {[
          { label: t("Vehicles Syncing"), value: health?.vehiclesSyncing ?? 0, color: "primary", icon: "fa-car" },
          { label: t("Stops Today"), value: health?.stopsToday ?? 0, color: "warning", icon: "fa-map-pin" },
          { label: t("Errors Last 24h"), value: health?.errorsLast24h ?? 0, color: "danger", icon: "fa-triangle-exclamation" },
          { label: t("Last GPS Sync"), value: health?.lastSync ? new Date(health.lastSync).toLocaleString() : "—", color: "success", icon: "fa-clock" },
        ].map(item => (
          <Col xs={12} md={6} xl={3} key={item.label}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="d-flex align-items-center gap-3">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center bg-${item.color} bg-opacity-10`}
                  style={{ width: 48, height: 48 }}
                >
                  <i className={`fas ${item.icon} text-${item.color}`} />
                </div>
                <div>
                  <div className="small text-muted">{item.label}</div>
                  <div className="fw-bold">{typeof item.value === "number" ? item.value.toLocaleString() : item.value}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white fw-semibold">
          <i className="fas fa-bolt me-2 text-muted" />
          {t("Manual Triggers")}
        </Card.Header>
        <Card.Body className="d-flex flex-wrap gap-2">
          {triggerActions.map(action => (
            <Button
              key={action.key}
              variant="outline-primary"
              size="sm"
              disabled={Boolean(isTriggering)}
              onClick={() => runTrigger(action.endpoint, action.key)}
            >
              <i className={`fas ${isTriggering === action.key ? "fa-spinner fa-spin" : action.icon} me-1`} />
              {t(action.labelKey)}
            </Button>
          ))}
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white fw-semibold">
              <i className="fas fa-satellite-dish me-2 text-muted" />
              {t("Providers")}
            </Card.Header>
            <Card.Body>
              {!health ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : health.providers.length === 0 ? (
                <div className="text-muted small">{t("No data available")}</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {health.providers.map(provider => (
                    <div key={provider.name} className="border rounded p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <strong className="text-capitalize">{provider.name}</strong>
                        <Badge bg={provider.healthy ? "success" : "danger"}>
                          {provider.healthy ? t("Healthy") : t("With Errors")}
                        </Badge>
                      </div>
                      <div className="small text-muted mt-1">
                        {t("Vehicles")}: {provider.vehicleCount.toLocaleString()}
                      </div>
                      <div className="small text-muted">
                        {t("Last Sync")}: {provider.lastSync ? new Date(provider.lastSync).toLocaleString() : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white fw-semibold">
              <i className="fas fa-filter me-2 text-muted" />
              {t("Log Filters")}
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-center">
              <Row className="g-3 align-items-center">
                <Col md={6}>
                  <Form.Label className="small text-muted mb-1">{t("Timer")}</Form.Label>
                  <Form.Select size="sm" value={selectedTimer} onChange={e => setSelectedTimer(e.target.value)}>
                    <option value="">{t("All Timers")}</option>
                    {availableTimers.map(timer => (
                      <option key={timer} value={timer}>{timer}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <div className="small text-muted mb-1">{t("Active Scope")}</div>
                  <Badge bg="light" text="dark" className="p-2 border fw-normal text-truncate w-100 text-start">
                    <i className="fas fa-building me-1 text-primary" />
                    {selectedOrganizationId ? (organizationMap[selectedOrganizationId] || selectedOrganizationId) : t("All Organizations")}
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="fas fa-list-check me-2 text-muted" />
            {t("Recent Sync Logs")}
          </span>
          <Badge bg="light" text="dark">{filteredLogs.length}</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : (
            <Table hover responsive className="mb-0 small">
              <thead className="table-light">
                <tr>
                  <th>{t("Timer")}</th>
                  <th>{t("Organization")}</th>
                  <th>{t("Started")}</th>
                  <th>{t("Duration")}</th>
                  <th className="text-end">{t("Read")}</th>
                  <th className="text-end">{t("Upserted")}</th>
                  <th className="text-end">{t("Skipped")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      {t("No sync logs found")}
                    </td>
                  </tr>
                ) : filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td className="fw-semibold">{log.timer}</td>
                    <td>{log.organizationId ? (organizationMap[log.organizationId] || log.organizationId) : "—"}</td>
                    <td>{new Date(log.startedAt).toLocaleString()}</td>
                    <td>{log.durationMs ? `${Math.round(log.durationMs / 1000)}s` : "—"}</td>
                    <td className="text-end">{log.recordsRead.toLocaleString()}</td>
                    <td className="text-end">{log.recordsUpserted.toLocaleString()}</td>
                    <td className="text-end">{log.recordsSkipped.toLocaleString()}</td>
                    <td>
                      {log.errorMessage ? (
                        <div>
                          <Badge bg="danger">{t("Error")}</Badge>
                          <div className="small text-danger mt-1" style={{ maxWidth: 340, whiteSpace: "normal" }}>
                            {log.errorMessage}
                          </div>
                        </div>
                      ) : (
                        <Badge bg="success">{t("OK")}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default SyncMonitorComponent
